'use client'

import { CreditCard } from 'lucide-react'
import { useEffect, useState } from 'react'
import { cn } from '../../lib/utils'
import { LoadCards } from '../pages/member/sell/actions/load-cards'
import { Button } from './ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog'

interface CreditCardModalProps {
  userId: any
}

export function CreditCardModal({ userId }: CreditCardModalProps) {
  const [open, setOpen] = useState(false)
  const { data: cards, loadCards } = LoadCards('VENDA')

  useEffect(() => {
    if (userId) loadCards(userId)
  }, [userId])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <CreditCard className="mr-2 h-4 w-4" />
          Cartão de Crédito
        </Button>
      </DialogTrigger>
      <DialogContent className={'sm:max-w-lg'}>
        <DialogHeader>
          <DialogTitle>Cartão de Crédito</DialogTitle>
        </DialogHeader>
        <div className="py-4 flex flex-col gap-2 max-h-[calc(100vh-300px)] overflow-y-auto">
          {cards && cards?.length > 0 ? (
            cards?.map((card: any) => {
              return <CreditCardDisplay card={card} />
            })
          ) : (
            <p className="text-center text-muted-foreground py-8">Nenhum cartão de crédito encontrado para este usuário.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface CardType {
  id: number
  numero: string
  bandeira: string
  token: string
  cliente: {
    id: number
    inclusao: string
    alteracao: any
    status: number
    idAcademia: number
    idUnidade: number
    idUsuario: number
    tipo: string
    integracaoId: string
    cpfCnpj: string
    email: any
    nome: any
  }
  label: string
  value: string
}

interface CreditCardProps {
  card: CardType
}

function CreditCardDisplay({ card }: CreditCardProps) {
  const getCardBackground = (type: string) => {
    switch (type.toLowerCase()) {
      case 'visa':
        return 'bg-gradient-to-r from-blue-600 to-blue-800'
      case 'mastercard':
        return 'bg-gradient-to-r from-red-600 to-orange-600'
      case 'amex':
        return 'bg-gradient-to-r from-teal-500 to-teal-700'
      case 'elo':
        return 'bg-gradient-to-r from-yellow-500 to-yellow-700'
      default:
        return 'bg-gradient-to-r from-gray-700 to-gray-900'
    }
  }

  const getCardLogo = (type: string) => {
    switch (type.toLowerCase()) {
      case 'visa':
        return 'VISA'
      case 'mastercard':
        return 'MASTERCARD'
      case 'amex':
        return 'AMEX'
      case 'elo':
        return 'ELO'
      default:
        return 'CARD'
    }
  }

  return (
    <div className="space-y-4">
      <div className={cn('relative rounded-xl p-6 text-white shadow-lg transition-all', getCardBackground(card.bandeira))}>
        <div className="absolute top-4 right-4 flex gap-2"></div>

        <div className="flex flex-col h-44 justify-between">
          <div className="flex justify-between items-start">
            <CreditCard className="h-8 w-8" />
            <span className="text-sm font-bold tracking-wider">{getCardLogo(card.bandeira)}</span>
          </div>

          <div className="mt-4">
            <p className="text-sm text-white/70 mb-1">Número do Cartão</p>
            <p className="font-mono text-lg tracking-wider">**** **** **** {card.numero}</p>
          </div>

          <div className="flex justify-between mt-4">
            <div>
              <p className="text-xs text-white/70 mb-1">Nome do Titular</p>
              <p className="font-medium tracking-wide">{card.cliente.nome || 'Nome indisponivel'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
